import { Check, Share2, ShieldOff, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { ShareDialog } from "@/components/sharing/ShareDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { usePhotoSelection } from "@/hooks/use-photo-selection";
import { useRevokeShares, useShares } from "@/hooks/use-shares";
import {
  SHARE_EFFECT_OPTIONS,
  SHARE_EFFECTS,
  SHARE_SCOPE_OPTIONS,
  SHARE_SCOPES,
} from "@/lib/enums";
import { formatDateTime } from "@/lib/format";
import type { ProjectDetail } from "@/schemas/photos";
import type { ShareRow, ShareSummary } from "@/schemas/shares";

function scopeLabel(row: ShareRow): string {
  return SHARE_SCOPE_OPTIONS.find((option) => option.value === row.scope)?.label ?? row.scope;
}

function ShareCheck({
  row,
  selected,
  onToggle,
}: {
  row: ShareRow;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      variant={selected ? "default" : "outline"}
      size="icon"
      role="checkbox"
      aria-checked={selected}
      aria-label={`Select the share for ${row.scopeName ?? "the whole project"}`}
      data-testid="share-select"
      onClick={onToggle}
      className="size-6 rounded-sm"
    >
      {selected && <Check className="size-3.5" />}
    </Button>
  );
}

/** Every active share on the project: audience, scope, who made it and when. Revokes in bulk. */
export function SharingTab({
  project,
  summary,
}: {
  project: ProjectDetail;
  summary: ShareSummary | undefined;
}) {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [scope, setScope] = useState("");
  const { data, isLoading, isError, error, refetch } = useShares(
    project.id,
    grid.query,
    grid.pageRequest,
    scope,
  );
  const [sharing, setSharing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const revoke = useRevokeShares();

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const selection = usePhotoSelection(total);
  const { clear } = selection;

  // A different filter is a different list, as on the Photos tab.
  const listKey = JSON.stringify([grid.query.search, grid.query.status, scope]);
  const lastListKey = useRef(listKey);
  useEffect(() => {
    if (lastListKey.current === listKey) return;
    lastListKey.current = listKey;
    clear();
  }, [listKey, clear]);

  const effect = SHARE_EFFECTS.find((value) => value === grid.status);
  const scopeFilter = SHARE_SCOPES.find((value) => value === scope);

  async function revokeSelected() {
    const request = selection.all
      ? {
          projectId: project.id,
          all: true as const,
          search: grid.query.search || undefined,
          effect,
          scope: scopeFilter,
          ...(selection.excluded.length > 0 ? { excludeIds: selection.excluded } : {}),
        }
      : { projectId: project.id, ids: selection.picked };
    const count = selection.count;
    await revoke.mutateAsync(request, {
      onSuccess: () => {
        toast.success(`Revoked ${count} ${count === 1 ? "share" : "shares"}`);
        clear();
        setConfirming(false);
      },
    });
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search audience, album, photo or person"
        className="w-full md:w-80"
      />
      <EnumSelect
        aria-label="State"
        value={grid.status}
        onValueChange={grid.setStatus}
        options={SHARE_EFFECT_OPTIONS}
        allOption={{ label: "All states" }}
        className="w-full md:w-40"
      />
      <EnumSelect
        aria-label="Scope"
        value={scope}
        onValueChange={(next) => {
          setScope(next);
          grid.setStatus(grid.status);
        }}
        options={SHARE_SCOPE_OPTIONS}
        allOption={{ label: "All scopes" }}
        className="w-full md:w-40"
      />
      <Button
        variant="outline"
        data-testid="share-project"
        disabled={!summary}
        onClick={() => setSharing(true)}
        className="w-full md:ml-auto md:w-auto"
      >
        <Share2 className="size-4" />
        Share whole project
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      {toolbar}
      {selection.count > 0 && (
        <div
          data-testid="share-selection-bar"
          className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 shadow-md"
        >
          <span data-testid="share-selection-count" className="text-sm font-medium tabular-nums">
            {selection.count.toLocaleString("en-US")} selected
          </span>
          {selection.count < total && (
            <Button variant="ghost" size="sm" onClick={selection.selectAll}>
              Select all {total.toLocaleString("en-US")}
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              data-testid="share-revoke"
              onClick={() => setConfirming(true)}
            >
              <ShieldOff className="size-4" />
              Revoke
            </Button>
            <Button variant="ghost" size="sm" onClick={clear}>
              <X className="size-4" />
              Clear
            </Button>
          </div>
        </div>
      )}
      {isError ? (
        <ErrorState
          message={error?.message ?? "Could not load the shares"}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          <DataTable
            headers={[
              { label: "Select", className: "w-16" },
              { label: "Audience", sortKey: "audience" },
              { label: "Scope", sortKey: "scope" },
              { label: "State", sortKey: "effect" },
              { label: "Created by", sortKey: "createdBy", className: "hidden md:table-cell" },
              { label: "Created", sortKey: "createdAt", className: "hidden md:table-cell" },
            ]}
            sort={grid.sort}
            sortDir={grid.sortDir}
            onSort={grid.toggleSort}
            loading={isLoading}
            empty={
              <EmptyState
                icon={Share2}
                title="Nothing is shared on this project"
                description="Share an album from the Albums tab."
              />
            }
          >
            {rows.map((row) => (
              <tr
                key={row.id}
                data-testid="share-row"
                className="border-b border-border last:border-0 hover:bg-muted/30"
              >
                <td className="px-4 py-3">
                  <ShareCheck
                    row={row}
                    selected={selection.isSelected(row.id)}
                    onToggle={() => selection.toggle(row.id)}
                  />
                </td>
                <td className="px-4 py-3 font-medium">
                  {row.audience}
                  {!row.hasAccount && (
                    <span data-testid="share-no-account" className="mt-1 block">
                      <Badge variant="warning">No IE account has this firm</Badge>
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-muted-foreground">{scopeLabel(row)}</span>
                  {row.scopeName && <span className="block break-words">{row.scopeName}</span>}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={row.effect === "include" ? "success" : "warning"}>
                    {row.effect === "include" ? "Shared" : "Excluded"}
                  </Badge>
                  {row.effect === "include" && row.scope !== "photo" && (
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {row.includeFuture ? "Takes new photos" : "Photos at share time"}
                    </span>
                  )}
                </td>
                <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                  {row.createdBy}
                </td>
                <td className="hidden px-4 py-3 whitespace-nowrap text-muted-foreground md:table-cell">
                  {formatDateTime(row.createdAt)}
                </td>
              </tr>
            ))}
          </DataTable>
          <CursorPagination
            total={total}
            page={grid.page}
            limit={DEFAULT_LIMIT}
            hasNext={data?.hasNext ?? false}
            hasPrev={data?.hasPrev ?? false}
            onNext={() => grid.goNext(data?.nextCursor ?? null)}
            onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
            loading={isLoading}
            itemLabel="shares"
          />
        </>
      )}
      {sharing && summary && (
        <ShareDialog
          open
          onOpenChange={setSharing}
          project={project}
          audiences={summary.audiences}
        />
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        variant="destructive"
        title={`Revoke ${selection.count} ${selection.count === 1 ? "share" : "shares"}?`}
        description="Access changes at once. Revoking an exclusion puts that photo back in view if its album or project is still shared, so revoke the share above it too if you want it gone."
        confirmLabel="Revoke"
        loading={revoke.isPending}
        onConfirm={revokeSelected}
      />
    </div>
  );
}
